import { formatList } from "@memorang/helpers/i18n";
import {
	isItemAnswered,
	isItemAnsweredCorrectly,
	isItemReviewable,
} from "@memorang/helpers/item";
import { getReportSkills } from "@memorang/helpers/report";
import { isProficiencyTag, isQuestionTypeTag } from "@memorang/helpers/tag";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import type { TFunction } from "i18next";
import { questionTypeIconMap, skillIconMap } from "../../../constants";
import type { LevelIconName } from "../../../icon";
import { flattenSessionItemsForLookup } from "../SessionReportQuestionTypeCard/helpers";

export const MAX_STATS_ICON_STACK = 4;

const hasNestedItems = (
	item: BaseItemSchema,
): item is BaseItemSchema & { items: BaseItemSchema[] } =>
	"items" in item && Array.isArray(item.items) && item.items.length > 0;

const flattenLeafItems = (items: BaseItemSchema[]): BaseItemSchema[] =>
	items.flatMap((item) =>
		hasNestedItems(item) ? flattenLeafItems(item.items) : [item],
	);

export const getAttemptedCounts = (
	sessionItems: BaseItemSchema[],
	isSummativeTest: boolean,
): { numAttempted: number; numCorrect: number; numTotal: number } => {
	const leafItems = flattenLeafItems(sessionItems).filter((item) =>
		isItemReviewable(item),
	);
	return {
		numAttempted: leafItems.filter((item) =>
			isItemAnswered({ item, isSummativeTest, inReviewMode: true }),
		).length,
		numCorrect: leafItems.filter((item) => isItemAnsweredCorrectly(item))
			.length,
		numTotal: leafItems.length,
	};
};

export type DimensionEntity = {
	title: string;
	icon: LevelIconName;
};

type StatsRowTileBase = {
	key: string;
	label: string;
	value: string | number;
	suffix?: string;
	tooltip?: string;
};

export type StatsRowTile =
	| (StatsRowTileBase & {
			icon: LevelIconName;
			icons?: never;
	  })
	| (StatsRowTileBase & {
			icon?: never;
			icons: LevelIconName[];
	  });

type DimensionStat =
	| {
			value: string;
			label: string;
			icon: LevelIconName;
			icons?: never;
			tooltip?: string;
	  }
	| {
			value: string;
			label: string;
			icon?: never;
			icons: LevelIconName[];
			tooltip?: string;
	  };

const SKILL_FALLBACK: LevelIconName = "book-open-page-variant-outline";
const QUESTION_TYPE_FALLBACK: LevelIconName = "format-list-bulleted-type";
const PROFICIENCY_FALLBACK: LevelIconName = "pine-tree";

export const collectSkillEntities = (
	tags: MixedItemTag[],
	topics: string[],
): DimensionEntity[] =>
	getReportSkills(tags, topics).map((tag) => ({
		title: tag.title,
		icon: skillIconMap[tag.title] ?? SKILL_FALLBACK,
	}));

export const collectQuestionTypeEntities = (
	tags: MixedItemTag[],
): DimensionEntity[] =>
	tags.filter(isQuestionTypeTag).map((tag) => ({
		title: tag.title,
		icon: questionTypeIconMap[tag.title] ?? QUESTION_TYPE_FALLBACK,
	}));

export const collectProficiencyEntities = ({
	tags,
	sessionItems,
	proficiencyIconMap = {},
}: {
	tags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	proficiencyIconMap?: Record<string, LevelIconName>;
}): DimensionEntity[] => {
	const proficiencyTags = tags.filter(isProficiencyTag);
	const validTagTitles = proficiencyTags
		.map((tag) => tag.title.trim())
		.filter(Boolean);
	if (validTagTitles.length > 0) {
		return validTagTitles.map((title) => ({
			title,
			icon: proficiencyIconMap[title] ?? PROFICIENCY_FALLBACK,
		}));
	}

	const seen = new Set<string>();
	const entities: DimensionEntity[] = [];
	for (const item of flattenSessionItemsForLookup(sessionItems)) {
		const title = item.metadata?.proficiency?.trim();
		if (!title) {
			continue;
		}
		const key = title.toLowerCase();
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		entities.push({
			title,
			icon: proficiencyIconMap[title] ?? PROFICIENCY_FALLBACK,
		});
	}
	return entities;
};

export const deriveDimensionStat = ({
	entities,
	singularLabel,
	pluralLabel,
	mixedLabel,
	locale,
	maxStack = MAX_STATS_ICON_STACK,
}: {
	entities: DimensionEntity[];
	singularLabel: string;
	pluralLabel: string;
	mixedLabel: string;
	locale: Intl.LocalesArgument;
	maxStack?: number;
}): DimensionStat | null => {
	if (entities.length === 0) {
		return null;
	}
	if (entities.length === 1) {
		const [entity] = entities;
		if (!entity) {
			return null;
		}
		return {
			value: entity.title,
			label: singularLabel,
			icon: entity.icon,
		};
	}
	return {
		value: mixedLabel,
		label: pluralLabel,
		icons: entities.slice(0, maxStack).map((entity) => entity.icon),
		tooltip: formatList(
			locale,
			entities.map((entity) => entity.title),
		),
	};
};

export type OverallPoints = {
	earned: number;
	total: number;
};

type BuildStatsRowTilesArgs = {
	isSummativeTest: boolean;
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
	timeElapsed: number;
	showCorrectStat?: boolean;
	overallPoints?: OverallPoints;
	proficiencyIconMap?: Record<string, LevelIconName>;
	formatTime: (seconds: number) => string;
	locale: Intl.LocalesArgument;
	t: TFunction;
};

export const buildStatsRowTiles = ({
	isSummativeTest,
	tags,
	topics,
	sessionItems,
	timeElapsed,
	showCorrectStat,
	overallPoints,
	proficiencyIconMap,
	formatTime,
	locale,
	t,
}: BuildStatsRowTilesArgs): StatsRowTile[] => {
	const tiles: StatsRowTile[] = [];
	const mixedLabel = t("session.results.summary.mixedLevel");

	const skills = deriveDimensionStat({
		entities: collectSkillEntities(tags, topics),
		singularLabel: t("session.results.summary.stats.skill"),
		pluralLabel: t("session.results.summary.stats.skills"),
		mixedLabel,
		locale,
	});
	if (skills) {
		tiles.push({ key: "skills", ...skills });
	}

	if (!isSummativeTest) {
		const questionTypes = deriveDimensionStat({
			entities: collectQuestionTypeEntities(tags),
			singularLabel: t("session.results.summary.stats.questionType"),
			pluralLabel: t("session.results.summary.stats.questionTypes"),
			mixedLabel,
			locale,
		});
		if (questionTypes) {
			tiles.push({ key: "questionTypes", ...questionTypes });
		}

		const proficiencyLevels = deriveDimensionStat({
			entities: collectProficiencyEntities({
				tags,
				sessionItems,
				proficiencyIconMap,
			}),
			singularLabel: t("session.results.summary.stats.proficiencyLevel"),
			pluralLabel: t("session.results.summary.stats.proficiencyLevels"),
			mixedLabel,
			locale,
		});
		if (proficiencyLevels) {
			tiles.push({ key: "proficiencyLevels", ...proficiencyLevels });
		}
	}

	const { numAttempted, numCorrect, numTotal } = getAttemptedCounts(
		sessionItems,
		isSummativeTest,
	);
	const ofTotalSuffix = t("session.results.summary.stats.ofTotal", {
		total: numTotal,
	});
	tiles.push({
		key: "itemsAttempted",
		icon: "card-multiple-outline",
		label: t("session.results.summary.stats.itemsAttempted", {
			count: numTotal,
		}),
		value: String(numAttempted),
		suffix: ofTotalSuffix,
	});

	if (showCorrectStat) {
		tiles.push(
			overallPoints
				? {
						key: "pointsEarned",
						icon: "check-circle-outline",
						label: t("session.results.summary.stats.pointsEarned", {
							count: Math.round(overallPoints.total),
						}),
						value: String(Math.round(overallPoints.earned)),
						suffix: t("session.results.summary.stats.ofPointsTotal", {
							total: Math.round(overallPoints.total),
						}),
					}
				: {
						key: "correct",
						icon: "check-circle-outline",
						label: t("session.results.summary.stats.answeredCorrectly"),
						value: String(numCorrect),
						suffix: ofTotalSuffix,
					},
		);
	}

	if (isSummativeTest) {
		tiles.push({
			key: "timeTaken",
			icon: "clock-outline",
			label: t("session.results.summary.stats.timeTaken"),
			value: formatTime(timeElapsed),
		});
	}

	return tiles;
};
